import { useEffect, useRef, useState } from 'react';
import { useTabs, ToolTab as ToolTabType } from '../context/TabContext';
import { useToolbox } from '../context/ToolboxContext';
import { ToolboxTab } from './ToolboxTab';
import { ToolTab } from './ToolTab';
import { SuggestedToolsTab } from './SuggestedToolsTab';

export function RightPanel() {
  const { tabs, activeTabId } = useTabs();
  const { markToolsAsSeen, filledStagesCount } = useToolbox();
  const activeTab = tabs.find(t => t.id === activeTabId);
  const panelRef = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState(false);
  const prevActiveTabId = useRef(activeTabId);

  // If no active tab, default to toolbox
  const displayTab = activeTab || tabs.find(t => t.type === 'toolbox') || tabs[0];

  // Mark tools as seen when Toolbox tab is opened
  useEffect(() => {
    if (activeTabId === 'toolbox') {
      markToolsAsSeen();
    }
  }, [activeTabId, markToolsAsSeen]);

  // Scroll to panel and highlight when a tool tab is opened
  useEffect(() => {
    if (activeTabId && activeTabId !== prevActiveTabId.current && activeTabId.startsWith('tool-')) {
      // Scroll to the top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Flash highlight
      setHighlight(true);
      const timer = setTimeout(() => setHighlight(false), 2000);
      prevActiveTabId.current = activeTabId;
      return () => clearTimeout(timer);
    }
    prevActiveTabId.current = activeTabId;
  }, [activeTabId]);

  // Ensure we always have a tab to display
  if (!displayTab) {
    return (
      <div className="h-full flex flex-col bg-white border border-neutral-200 rounded-card shadow-card overflow-hidden">
        <div className="flex-1 flex items-center justify-center">
          <p className="text-neutral-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className={`h-full flex flex-col bg-white border border-neutral-200 rounded-card shadow-card overflow-hidden transition-shadow duration-700 ${highlight ? 'ring-2 ring-primary-400 shadow-lg shadow-primary-100' : ''}`}
    >
      {/* Tab Navigation */}
      {tabs.length > 0 && (
        <div className="flex-shrink-0 border-b border-neutral-200 bg-neutral-50 overflow-x-auto">
          <div className="flex min-w-0">
            {tabs.map(tab => (
              <TabButton key={tab.id} tab={tab} />
            ))}
          </div>
        </div>
      )}

      {/* Tab Content — allow scroll passthrough when panels are empty */}
      <div className={`flex-1 ${
        (displayTab.type === 'toolbox' && filledStagesCount === 0) || displayTab.type === 'suggested'
          ? 'overflow-y-hidden' : 'overflow-y-auto overscroll-contain'
      }`}>
        {displayTab.type === 'toolbox' && <ToolboxTab />}
        {displayTab.type === 'suggested' && <SuggestedToolsTab />}
        {displayTab.type === 'tool' && displayTab.toolId && (
          <ToolTab toolId={displayTab.toolId} />
        )}
        {displayTab.type !== 'toolbox' && displayTab.type !== 'suggested' && displayTab.type !== 'tool' && (
          <div className="p-6">
            <p className="text-neutral-500">Unknown tab type</p>
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({ tab }: { tab: ToolTabType }) {
  const { setActiveTab, closeTab } = useTabs();
  const { unseenNewToolsCount } = useToolbox();

  // Show badge only for Toolbox tab when there are unseen new tools
  const showBadge = tab.type === 'toolbox' && unseenNewToolsCount > 0;

  return (
    <button
      onClick={() => setActiveTab(tab.id)}
      className={`
        relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 min-w-[120px] max-w-[200px]
        ${tab.canClose ? 'justify-between' : 'justify-center'}
        ${
          tab.isActive
            ? 'border-primary-600 text-primary-600 bg-white'
            : 'border-transparent text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100'
        }
      `}
    >
      <span className="truncate">{tab.title}</span>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {showBadge && (
          <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-red-500 text-white text-xs font-medium rounded-pill">
            {unseenNewToolsCount > 99 ? '99+' : unseenNewToolsCount}
          </span>
        )}
        {tab.canClose && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeTab(tab.id);
            }}
            className={`
              p-0.5 rounded hover:bg-neutral-200 transition-colors flex-shrink-0
              ${tab.isActive ? 'text-neutral-600' : 'text-neutral-400'}
            `}
            title="Close tab"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </button>
  );
}
